import { NextPage } from "next"
import Error from "next/error"
import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import AutoLink from "~/components/atoms/AutoLink"
import TransitionLink from "~/components/atoms/TransitionLink"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import DetailMenu from "~/components/organisms/DetailMenu"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import RecruitmentHead from "~/components/organisms/RecruitmentHead"
import SchduleCard from "~/components/organisms/SchduleCard"
import { IsPC, IsMobile, SpacingSize, Color, Z, TextSize, LineHeight } from "~/consts/style-mixins"
import ObservedActionBar from "~/observers/ActionBar"
import { internalLink } from "~/services/helper"
import { InfraNextPageContext } from "~/types/InfraNextPageContext"
import { ReturnGetInitialProps } from "~/types/ReturnGetInitialProps"

const getInitialProps = async ({ initialStoreData: { eventStore }, query, res }: InfraNextPageContext) => {
  const id = Number(query["event-id"])
  const event = await eventStore.fetchById(id)
  const sortedEventSchedules = event?.sortedSchedules
  if (!event || !event.id) {
    res.statusCode = 404
  }
  return { event, sortedEventSchedules }
}

type GetInitialProps = ReturnGetInitialProps<typeof getInitialProps>

const EventDetailPage: NextPage<GetInitialProps> = ({ event, sortedEventSchedules }) => {
  if (!event || !event.id) {
    return <Error statusCode={404} />
  }

  const crumbs = [
    {
      name: "イベント・説明会",
      link: internalLink(`/events`),
    },
    {
      name: event.title,
      link: internalLink(`/events/${event.id}`),
    },
  ]

  return (
    <>
      <Head>
        <title>{event.title} | Infra（インフラ）</title>
        <meta name="description" content={`${event.content?.replace(/\r?\n/g, "").slice(0, 130)}…`} />
        <meta name="og:title" content={`${event.title} | Infra（インフラ）`} />
        <meta name="og:description" content={`${event.content?.replace(/\r?\n/g, "").slice(0, 130)}…`} />
        <meta name="og:image" content={event.thumbnailImgUrl} />
      </Head>
      <Header showTab selectedTab="Event" logoType="Events" />
      <Container size="Large">
        <StyledInnerContainer>
          <StyledDetailMenuWrapper>
            <DetailMenu company={event.company} selectedTabIndex={6} />
          </StyledDetailMenuWrapper>
          <StyledMainContentWrapper>
            <StyledMainContentInner>
              <StyledRecruitmentHead category="event" recruitment={event} />
              <div>
                <StyledEventScheduleWrapper>
                  <StyledHeading type="SubHero">日程</StyledHeading>
                  {sortedEventSchedules?.map((schedule, index) => {
                    return (
                      <StyledEventSchduleCardWrapper key={index} afterDeadline={!schedule.beforeEntryDeadline}>
                        <SchduleCard key={index} event={event} schedule={schedule} externalApplication={false} />
                      </StyledEventSchduleCardWrapper>
                    )
                  })}
                </StyledEventScheduleWrapper>
                <StyledEachItemWrapper>
                  <StyledAutoLink>{event.content ?? ""}</StyledAutoLink>
                </StyledEachItemWrapper>
              </div>
              <StyledTransitionLink
                href={internalLink(`/companies/${event.company.id}/recruitments`)}
                size="Large"
                isButton
                icon="Back"
              >
                この会社の他の募集を見る
              </StyledTransitionLink>
            </StyledMainContentInner>
          </StyledMainContentWrapper>
        </StyledInnerContainer>
      </Container>
      <Breadcrumb crumbs={crumbs} />
      <ActionBarWrapper>
        <ObservedActionBar type="event" id={event.id} status={event.status} schedules={sortedEventSchedules} />
      </ActionBarWrapper>
      <Footer />
    </>
  )
}

EventDetailPage.getInitialProps = getInitialProps

export default EventDetailPage

interface StyledProps {
  afterDeadline: boolean
}

const StyledInnerContainer = styled.div`
  ${IsPC(css`
    padding: 48px 0;
    display: flex;
    flex-wrap: wrap;
  `)}
`

const StyledDetailMenuWrapper = styled.div`
  position: sticky;
  z-index: ${Z.Tab};
  ${IsPC(css`
    top: 16px;
    height: 100%;
  `)}
  ${IsMobile(css`
    top: 0;
  `)}
`

const StyledMainContentWrapper = styled.div`
  width: 100%;
  ${IsPC(css`
    width: calc(100% - 280px);
  `)}
  ${IsMobile(css`
    padding: 24px 0 32px;
  `)}
`

const StyledMainContentInner = styled.div`
  ${IsPC(css`
    padding-left: 48px;
  `)}
`

const StyledRecruitmentHead = styled(RecruitmentHead)`
  margin-bottom: ${SpacingSize.XL}px;
`

const StyledTransitionLink = styled(TransitionLink)`
  max-width: 280px;
  width: 100%;
  margin: ${SpacingSize["2XL"]}px auto 0;
  display: block;
`

const StyledEachItemWrapper = styled.div`
  margin-bottom: ${SpacingSize["2XL"]}px;
`

const StyledAutoLink = styled(AutoLink)`
  font-size: ${TextSize.M}px;
  line-height: ${LineHeight.L}%;
  white-space: pre-line;
  word-break: break-all;
`

const StyledHeading = styled(Typography)`
  margin-bottom: ${SpacingSize.XS}px;
  color: ${Color.Primary};
`

const StyledEventScheduleWrapper = styled.div`
  margin-bottom: ${SpacingSize.XL}px;
`

const StyledEventSchduleCardWrapper = styled.div`
  margin-top: 12px;
  ${({ afterDeadline }: StyledProps) => `
    ${
      afterDeadline &&
      `
      text-decoration: line-through;
    `
    }
  `}
`

const ActionBarWrapper = styled.div`
  position: fixed;
  bottom: 16px;
  left: calc(50% + 164px - 200px);
  width: 100%;
  max-width: 400px;
  z-index: ${Z.Fixed};
  ${IsMobile(css`
    left: 0;
    max-width: inherit;
  `)}
`
